import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errText } from "../../lib/api";
import { FnfCard } from "../../components/admin/fnf/FnfCard";

export default function Fnf() {
  const [list, setList] = useState([]);
  const [emps, setEmps] = useState([]);
  const [form, setForm] = useState({ user_id: "", last_working_day: "", remarks: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/fnf").then((r) => setList(r.data)), []);
  useEffect(() => {
    load();
    api.get("/employees").then((r) => setEmps(r.data.filter((e) => e.status === "active" && e.role !== "super_admin")));
  }, [load]);

  const compute = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/fnf", form);
      toast.success("Settlement computed (draft)");
      setForm({ user_id: "", last_working_day: "", remarks: "" });
      load();
    } catch (err) { toast.error(errText(err)); } finally { setBusy(false); }
  };

  const settle = async (id) => {
    try {
      await api.put(`/fnf/${id}/settle`);
      toast.success("Settlement marked as settled. Employee deactivated.");
      load();
    } catch (e) { toast.error(errText(e)); }
  };

  const del = async (id) => {
    try {
      await api.delete(`/fnf/${id}`);
      toast.success("Draft deleted");
      load();
    } catch (e) { toast.error(errText(e)); }
  };

  return (
    <div className="space-y-6" data-testid="fnf-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Full &amp; Final settlement</h1>
        <p className="text-stone-500 text-sm mt-1">Compute exit dues: pro-rated final salary, EL encashment, gratuity and statutory deductions.</p>
      </div>

      <form onSubmit={compute} className="bg-white border border-stone-200 rounded-lg p-6 flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[220px]">
          <label className="text-sm font-semibold text-stone-700">Employee</label>
          <select required value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} data-testid="fnf-employee-select"
            className="mt-1 w-full min-h-[44px] px-3 rounded-lg border border-stone-300 bg-white">
            <option value="">Select employee…</option>
            {emps.map((e) => <option key={e.id} value={e.id}>{`${e.name} — ${e.employee_code}`}</option>)}
          </select>
        </div>
        <div>
          <label className="text-sm font-semibold text-stone-700">Last working day</label>
          <input type="date" required value={form.last_working_day} onChange={(e) => setForm({ ...form, last_working_day: e.target.value })} data-testid="fnf-lwd-input"
            className="mt-1 block min-h-[44px] px-3 rounded-lg border border-stone-300 bg-white" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="text-sm font-semibold text-stone-700">Remarks (optional)</label>
          <input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} data-testid="fnf-remarks-input"
            placeholder="Resignation, notice served…"
            className="mt-1 w-full min-h-[44px] px-3 rounded-lg border border-stone-300" />
        </div>
        <button type="submit" disabled={busy} data-testid="fnf-compute-button"
          className="tap-scale px-6 py-3 rounded-full bg-primary text-white font-bold hover:bg-[#A63F07] transition-colors duration-200 disabled:opacity-60">
          {busy ? "Computing…" : "Compute settlement"}
        </button>
      </form>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {list.length === 0 && (
          <p className="text-stone-400 text-sm lg:col-span-2 text-center py-10">No settlements yet. Compute one above.</p>
        )}
        {list.map((f) => <FnfCard key={f.id} f={f} onSettle={settle} onDelete={del} />)}
      </div>
    </div>
  );
}
